import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../..';
import { decimalToFixed2 } from '../../../../config/utils/calcUtils';
import { shouldShowTaxWithhold } from '../../../../config/utils/salesHelper';
import { CURRENCY } from '../../../../interfaces/enumerables';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { useDocValues } from '../../../../services/hooks/useDocValues';

interface Props {
  doc: InvoiceInterface;
}

export const PdfTotalCost = ({ doc }: Props) => {
  const { t } = useTranslation();
  const docValues = useDocValues(doc.products, doc.vatExceptionCategoryId, doc?.taxWithholdPercentage);

  const getVatValuesView = useCallback(() => {
    return docValues?.valuesByVatCategory?.map?.((cat, index) => {
      return (
        <View
          style={[styles.container1, styles.width100, pdfStyles.smallTitleRegular]}
          key={`${cat.percentage}-${index}`}>
          <Text style={[styles.width64]}>{cat.percentage}%</Text>
          <Text style={[styles.width180, styles.textAlignEnd]}>
            {decimalToFixed2(cat.cleanCost)} {CURRENCY.EUR}
          </Text>
          <Text style={[styles.width120, styles.textAlignEnd]}>
            {decimalToFixed2(cat.discount)} {CURRENCY.EUR}
          </Text>
          <Text style={[styles.width180, styles.textAlignEnd]}>
            {decimalToFixed2(cat.finalCleanCost)} {CURRENCY.EUR}
          </Text>
          <Text style={[styles.width120, styles.textAlignEnd]}>
            {decimalToFixed2(cat.total)} {CURRENCY.EUR}
          </Text>
        </View>
      );
    });
  }, [docValues?.valuesByVatCategory]);

  /**
   * Customer logic to hide value of percentage for 14%
   * We are doing that for one Customer that want to add 2 tax withholds in one line (8% + 6%)
   */
  const getTaxWithholdPercentage = useCallback(() => {
    // Disabled for now since 14% is not available in prod
    // if (doc?.taxWithholdPercentage !== '14.00')
    return <Text style={pdfStyles.smallTitleRegular}>({doc?.taxWithholdPercentage}%)</Text>;
  }, [doc?.taxWithholdPercentage]);

  const getTaxWithholdSection = useCallback(() => {
    if (shouldShowTaxWithhold(doc)) {
      return (
        <View style={[styles.container1, styles.width100, styles.paddingT6]}>
          <View>
            <View style={pdfStyles.flexStartCenterRow}>
              <Text style={[pdfStyles.smallTitle, styles.paddingR4]}>{t('SALE.TAX_WITHHOLD')}</Text>
              {getTaxWithholdPercentage()}
            </View>
            <Text style={pdfStyles.smallTitleRegular}>({t(`TAX_WITHHOLD.${doc?.taxWithhold?.name}_REASON`)})</Text>
          </View>
          <Text style={pdfStyles.smallTitle}>
            -{docValues?.taxWithholdAmount} {CURRENCY.EUR}
          </Text>
        </View>
      );
    }
  }, [doc, t, docValues?.taxWithholdAmount, getTaxWithholdPercentage]);

  return (
    <View style={[styles.container, pdfStyles.simpleBorder]}>
      <View style={[styles.container1, styles.width100, styles.paddingB6, pdfStyles.smallTitle]}>
        <View style={[styles.width64]}>
          <Text>{t('SALE.VAT')} %</Text>
        </View>
        <View style={[styles.width180, styles.textAlignEnd]}>
          <Text>{t('SALE.CLEAN_COST_BEFORE_DISCOUNT')}</Text>
        </View>
        <View style={[styles.width120, styles.textAlignEnd]}>
          <Text>{t('SALE.DISCOUNT')}</Text>
        </View>
        <View style={[styles.width180, styles.textAlignEnd]}>
          <Text>{t('SALE.CLEAN_COST')}</Text>
        </View>
        <View style={[styles.width120, styles.textAlignEnd]}>
          <Text>
            {t('SALE.VAT')} {CURRENCY.EUR}
          </Text>
        </View>
      </View>
      {getVatValuesView()}
      <View style={styles.separator} />
      <View style={[styles.container1, styles.width100, styles.paddingB6, pdfStyles.smallTitle]}>
        <Text>{t('SALE.TOTAL_CLEAN_VALUE')}</Text>
        <Text>
          {docValues?.cleanValueOfProducts} {CURRENCY.EUR}
        </Text>
      </View>
      <View style={[styles.container1, styles.width100, styles.paddingB6, pdfStyles.smallTitle]}>
        <Text>{t('SALE.TOTAL_VAT')}</Text>
        <Text>
          {docValues?.totalVat} {CURRENCY.EUR}
        </Text>
      </View>
      <View style={[styles.container1, styles.width100, pdfStyles.smallTitle]}>
        <Text>{t('SALE.TOTAL')}</Text>
        <Text>
          {docValues?.totalGrossAmount} {CURRENCY.EUR}
        </Text>
      </View>
      {getTaxWithholdSection()}
      <View style={[styles.container1, styles.width100, pdfStyles.smallTitle, styles.paddingT16]}>
        <Text>{t('SALE.PAYABLE_AMOUNT')}</Text>
        <Text>
          {docValues?.payableAmount} {CURRENCY.EUR}
        </Text>
      </View>
      {doc?.correlatedInvoices?.length ? (
        <View style={[styles.width100, pdfStyles.smallTitleRegular]}>
          <Text>
            <Text style={pdfStyles.smallTitle}>{t('SALE.CORRELATED_INVOICES_TITLE_VIEW')}: </Text>
            {doc.correlatedInvoices.map((c) => c.mark).join(', ')}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    width: 300,
    fontSize: 8,
  },
  container1: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'nowrap',
  },
  width100: {
    width: '100%',
  },
  width180: {
    width: 180,
  },
  width120: {
    width: 120,
  },
  width64: {
    width: 64,
  },
  paddingB6: {
    paddingBottom: 6,
  },
  paddingR4: {
    paddingRight: 4,
  },
  paddingT6: {
    paddingTop: 6,
  },
  paddingT16: {
    paddingTop: 16,
  },
  separator: {
    height: 0.1,
    backgroundColor: '#1e2023',
    width: '100%',
    marginTop: 4,
    marginBottom: 4,
  },
  textAlignEnd: {
    textAlign: 'right',
  },
});
